import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  ShoppingCart, 
  Trash2, 
  Eye, 
  ArrowLeft,
  Package,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import WishlistButton from '../components/ui/WishlistButton';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ui/ConfirmModal';

const WishlistPage = () => {
  const { t } = useLanguage();
  const { 
    items: wishlistItems, 
    loading, 
    error, 
    isEmpty,
    itemCount,
    clearWishlist,
    removeFromWishlist
  } = useWishlist();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [isClearing, setIsClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour ajouter des articles au panier');
      return;
    }
    
    try {
      await addToCart(product, 1);
      toast.success('Article ajouté au panier !');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  const handleRemoveFromWishlist = async (wishlistItemId) => {
    try {
      await removeFromWishlist(wishlistItemId);
      toast.success('Article retiré de votre liste de souhaits');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleClearWishlist = async () => {
    setIsClearing(true);
    try {
      await clearWishlist();
    } finally {
      setIsClearing(false);
    }
  };

  // États de la page
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 py-12 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <Heart className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">
              Connexion requise
            </h1>
            <p className="text-gray-300 mb-6">
              Veuillez vous connecter pour accéder à votre liste de souhaits
            </p>
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <span>Se connecter</span>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-12 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">
              Erreur de chargement
            </h1>
            <p className="text-gray-300 mb-6">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <span>Réessayer</span>
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <Heart className="h-12 w-12 text-yellow-500 mr-4" />
            <h1 className="text-4xl font-bold text-white">
              Ma Liste de Souhaits
            </h1>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Gardez une trace de vos bijoux préférés et ajoutez-les à votre panier quand vous êtes prêt
          </p>
        </motion.div>

        {/* Wishlist Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-gray-800 rounded-xl p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-yellow-500/20 p-3 rounded-full">
                <Heart className="h-8 w-8 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {itemCount} produit{itemCount > 1 ? 's' : ''}
                </h3>
                <p className="text-gray-400">
                  dans votre liste de souhaits
                </p>
              </div>
            </div>
            
            {!isEmpty && (
              <div className="flex space-x-3">
                <button 
                  onClick={() => setConfirmClear(true)}
                  disabled={isClearing}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>{isClearing ? 'Suppression...' : 'Vider la liste'}</span>
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Wishlist Content */}
        <AnimatePresence mode="wait">
          {isEmpty ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-gray-800 rounded-xl p-12">
                <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-4">
                  Votre liste de souhaits est vide
                </h3>
                <p className="text-gray-400 mb-6">
                  Commencez à explorer nos bijoux et ajoutez vos favoris à votre liste de souhaits
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  <span>Voir tous les produits</span>
                  <ArrowLeft className="h-5 w-5 rotate-180" />
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="items"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <AnimatePresence>
                  {wishlistItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 30, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -30, scale: 0.9 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 300
                      }}
                      className={`relative group ${
                        index % 4 !== 3 ? 'border-r border-[rgb(128,128,128)]' : ''
                      } ${
                        index < wishlistItems.length - 4 ? 'border-b border-[rgb(128,128,128)]' : ''
                      }`}
                    >
                      {/* Product Card */}
                      <div className="bg-gray-800 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                        {/* Image Container */}
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={item.primary_image || item.product?.main_photo || 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center'}
                            alt={item.name_fr || item.product?.name_fr}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center';
                            }}
                          />
                          
                          {/* Wishlist Button */}
                          <div className="absolute top-3 right-3">
                            <WishlistButton 
                              product={item} 
                              variant="filled"
                              size={18}
                            />
                          </div>
                          
                          {/* Overlay with actions */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleAddToCart(item.product || item)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 p-3 rounded-full transition-colors"
                                title="Ajouter au panier"
                              >
                                <ShoppingCart className="h-5 w-5" />
                              </button>
                              
                              {/* Bouton pour retirer de la wishlist */}
                              <button
                                onClick={() => handleRemoveFromWishlist(item.id)}
                                className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-full transition-colors"
                                title="Retirer de la liste de souhaits"
                              >
                                <Heart className="h-5 w-5 fill-current" />
                              </button>
                              
                              <Link
                                to={`/products/${item.product_id}`}
                                className="bg-white hover:bg-gray-100 text-gray-900 p-3 rounded-full transition-colors"
                                title="Voir le produit"
                              >
                                <Eye className="h-5 w-5" />
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="p-4">
                          <div className="mb-2">
                            <span className="inline-block bg-yellow-500/20 text-yellow-400 text-xs px-2 py-1 rounded-full">
                              {item.category_name || item.product?.category_name || 'Collection Perse'}
                            </span>
                          </div>
                          
                          <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                            {item.name_fr || item.product?.name_fr}
                          </h3>
                          
                          {/* Price */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              {item.sale_price && parseFloat(item.sale_price) > 0 ? (
                                <>
                                  <span className="text-yellow-500 font-bold text-lg">
                                    {parseFloat(item.sale_price).toFixed(2)} €
                                  </span>
                                  <span className="text-gray-400 line-through">
                                    {parseFloat(item.price || 0).toFixed(2)} €
                                  </span>
                                </>
                              ) : (
                                <span className="text-yellow-500 font-bold text-lg">
                                  {parseFloat(item.price || 0).toFixed(2)} €
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Stock Status */}
                          <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                              (item.in_stock || item.product?.in_stock) > 0 
                                ? 'text-green-400' 
                                : 'text-red-400'
                            }`}>
                              {(item.in_stock || item.product?.in_stock) > 0 
                                ? `${item.in_stock || item.product?.in_stock} en stock` 
                                : 'Rupture de stock'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Actions */}
        {!isEmpty && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <div className="bg-gray-800 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-white mb-4">
                Continuez vos achats
              </h3>
              <p className="text-gray-400 mb-6">
                Découvrez plus de bijoux élégants et ajoutez-les à votre liste de souhaits
              </p>
              <Link
                to="/products"
                className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors duration-300"
              >
                <span>Voir tous les produits</span>
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>

    {/* Confirm Clear Wishlist Modal */}
    <ConfirmModal
      isOpen={confirmClear}
      title="Vider la liste de souhaits"
      message="Êtes-vous sûr de vouloir vider votre liste de souhaits ? Cette action est irréversible."
      confirmText="Vider"
      cancelText="Annuler"
      danger
      onCancel={() => setConfirmClear(false)}
      onConfirm={async () => {
        setConfirmClear(false);
        await handleClearWishlist();
      }}
    />
    </>
  );
};

export default WishlistPage; 
